import React, { useState, useEffect, useRef } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../_helper/currencyContext";

const CurrencyAmountInput = ({
    label = "Amount",
    amount,                  // number | null
    onAmountChange,          // (newAmount: number) => void
    onCurrencyChange,        // optional (newCurrency: string) => void
    disabled = false,
    errors = {},
    disableScrollProps = {},
    defaultCurrency,
    originalAmount,          // optional original amount in base currency
    paidInCurrency,          // settlement paid currency override
    settlementData           // for remainingAmounts logic
}) => {
    const {
        currency: defaultCurrencyFromContext,
        allowedCurrencies,
        getCurrencySymbol,
        convertCurrency
    } = useCurrency();

    const { t } = useTranslation();
    // Priority: paidInCurrency > defaultCurrency prop > context default
    const baseCurrency = paidInCurrency || defaultCurrency || defaultCurrencyFromContext;

    // Local UI states
    const [localCurrency, setLocalCurrency] = useState(baseCurrency);
    const [inputValue, setInputValue] = useState("");

    // Refs
    const prevCurrencyRef = useRef(baseCurrency);
    const originalAmountRef = useRef(originalAmount ?? amount);

    // ─────────────────────────────────────────────────────────────
    // Keep originalAmount ref in sync when parent updates it
    // ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (originalAmount !== undefined) {
            originalAmountRef.current = originalAmount;
        }
    }, [originalAmount]);

    // ─────────────────────────────────────────────────────────────
    // Sync external amount → formatted input display
    // ─────────────────────────────────────────────────────────────
    useEffect(() => {
        setInputValue(amount != null && !isNaN(amount) ? formatAmount(amount) : "");
    }, [amount]);

    // ─────────────────────────────────────────────────────────────
    // React to external baseCurrency change (e.g. settlement paid currency)
    // ─────────────────────────────────────────────────────────────
    useEffect(() => {
        const previous = prevCurrencyRef.current;

        setLocalCurrency(baseCurrency);

        // Restore original amount only when switching BACK to base currency
        if (previous !== baseCurrency && originalAmountRef.current != null) {
            onAmountChange(Number(originalAmountRef.current));
        }

        prevCurrencyRef.current = baseCurrency;
    }, [baseCurrency]);

    // ─────────────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────────────
    const formatAmount = (num) => {
        if (num == null || isNaN(num)) return "";
        return Number(num).toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2   // adjust if you need more (e.g. crypto)
        });
    };

    const parseAmount = (str) => {
        if (str === "" || str === "." || str === "-") return null;
        const cleaned = str.replace(/,/g, "");
        const num = Number(cleaned);
        return isNaN(num) ? null : num;
    };

    // ─────────────────────────────────────────────────────────────
    // Currency dropdown change
    // ─────────────────────────────────────────────────────────────
    // const handleCurrencyChange = (newCurrency) => {
    //     if (newCurrency === localCurrency) return;

    //     const oldCurrency = prevCurrencyRef.current;
    //     let newAmount = amount ?? 0;

    //     // CASE 1: Settlement remaining amounts (no conversion)
    //     if (settlementData?.remainingAmounts) {
    //         const remaining = settlementData.remainingAmounts[newCurrency];
    //         if (remaining !== undefined) {
    //             newAmount = Number(remaining);
    //         }
    //     }
    //     // CASE 2: Normal conversion logic
    //     else {
    //         if (newCurrency === baseCurrency) {
    //             // Switching back to base → restore original
    //             newAmount = originalAmountRef.current ?? amount ?? 0;
    //         } else if (oldCurrency === baseCurrency) {
    //             // Leaving base currency → remember original
    //             if (originalAmountRef.current == null) {
    //                 originalAmountRef.current = amount ?? 0;
    //             }
    //             newAmount = convertCurrency(amount ?? 0, oldCurrency, newCurrency);
    //         } else {
    //             // Non-base → Non-base
    //             newAmount = convertCurrency(amount ?? 0, oldCurrency, newCurrency);
    //         }
    //     }

    //     onAmountChange(Number(newAmount));
    //     setLocalCurrency(newCurrency);
    //     prevCurrencyRef.current = newCurrency;   // ← FIXED: was "nowCurrency"
    //     onCurrencyChange?.(newCurrency);
    // };

    // const handleCurrencyChange = (newCurrency) => {
    //     if (newCurrency === localCurrency) return;

    //     const currentAmount = amount ?? 0;

    //     let convertedAmount;

    //     // Primary behavior: Convert the currently shown amount to the new currency
    //     convertedAmount = convertCurrency(currentAmount, localCurrency, newCurrency);

    //     // Exception: When returning to base currency, restore the true original (useful after Full payment)
    //     if (newCurrency === baseCurrency && originalAmountRef.current != null) {
    //         convertedAmount = originalAmountRef.current;
    //     }

    //     // Round to 2 decimals to prevent floating point issues
    //     const finalAmount = Number(convertedAmount?.toFixed(2));

    //     onAmountChange(finalAmount);
    //     setLocalCurrency(newCurrency);
    //     prevCurrencyRef.current = newCurrency;
    //     onCurrencyChange?.(newCurrency);
    // };

    const handleCurrencyChange = (newCurrency) => {
        if (newCurrency === localCurrency) return;

        const currentAmount = amount ?? 0;

        let convertedAmount = convertCurrency(currentAmount, localCurrency, newCurrency);

        // Safety: if conversion failed (undefined, null, NaN), keep current amount
        if (convertedAmount == null || isNaN(convertedAmount)) {
            convertedAmount = currentAmount;
        }

        // Exception: returning to base currency → restore original if available
        if (newCurrency === baseCurrency && originalAmountRef.current != null) {
            convertedAmount = originalAmountRef.current;
        }

        // Now safely round to 2 decimals
        const finalAmount = Number(convertedAmount.toFixed(2));

        onAmountChange(finalAmount);
        setLocalCurrency(newCurrency);
        prevCurrencyRef.current = newCurrency;
        onCurrencyChange?.(newCurrency);
    };

    // ─────────────────────────────────────────────────────────────
    // Amount input change (typing)
    // ─────────────────────────────────────────────────────────────
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);

        const parsed = parseAmount(value);
        if (parsed !== null || value === "" || value === "." || value === "-") {
            const finalValue = parsed ?? 0;

            onAmountChange(finalValue);

            // Update original reference when editing in base currency
            if (localCurrency === baseCurrency) {
                originalAmountRef.current = finalValue;
            }
        }
    };

    // ─────────────────────────────────────────────────────────────
    // On blur → clean up formatting
    // ─────────────────────────────────────────────────────────────
    const handleBlur = () => {
        const parsed = parseAmount(inputValue);
        const finalAmount = parsed !== null && parsed >= 0 ? parsed : 0;

        onAmountChange(finalAmount);
        setInputValue(formatAmount(finalAmount));

        if (localCurrency === baseCurrency) {
            originalAmountRef.current = finalAmount;
        }
    };

    // ─────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────
    return (
        <Form.Group className="mb-3">
            <Form.Label>{label}</Form.Label>

            <InputGroup>
                <Form.Control
                    type="text"
                    inputMode="decimal"           // better mobile keyboard
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={disabled}
                    isInvalid={!!errors.amount}
                    placeholder="0.00"
                    {...disableScrollProps}
                />

                <Form.Select
                    value={localCurrency}
                    style={{ maxWidth: "110px" }}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                // disabled={disabled}
                >
                    {allowedCurrencies.map((cur) => (
                        <option key={cur} value={cur}>
                            {cur}
                        </option>
                    ))}
                </Form.Select>

                <Form.Control.Feedback type="invalid">
                    {errors.amount}
                </Form.Control.Feedback>
            </InputGroup>

            {/* Show original amount when viewing in alternative currency */}
            {/* {localCurrency !== baseCurrency && originalAmountRef.current != null && (
                <Form.Text className="text-muted">
                    {t("Original amount")}: {getCurrencySymbol(baseCurrency)}{formatAmount(originalAmountRef.current)}
                </Form.Text>
            )} */}
        </Form.Group>
    );
};

export default CurrencyAmountInput;